'use client'

import { useEffect, useRef, useState } from 'react'
import { AlertCircle, ArrowLeft, CheckCircle2, Eye, EyeOff, Loader2, Mail } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

type Step = 'details' | 'code' | 'success'
type Status = 'idle' | 'loading' | 'error'

export function RegisterForm() {
  const [step, setStep] = useState<Step>('details')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [code, setCode] = useState('')
  const [status, setStatus] = useState<Status>('idle')
  const [error, setError] = useState('')
  const [cooldown, setCooldown] = useState(0)
  const codeInputRef = useRef<HTMLInputElement>(null)

  const inputClass =
    'w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-ring focus:ring-2 focus:ring-ring/30 disabled:opacity-50'

  useEffect(() => {
    if (step === 'code') codeInputRef.current?.focus()
  }, [step])

  useEffect(() => {
    if (cooldown <= 0) return
    const t = window.setTimeout(() => setCooldown((v) => v - 1), 1000)
    return () => window.clearTimeout(t)
  }, [cooldown])

  async function post(path: string, body: unknown) {
    const res = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    const data = (await res.json().catch(() => ({}))) as { error?: string }
    if (!res.ok) throw new Error(data.error || 'Something went wrong. Please try again.')
    return data
  }

  async function handleDetailsSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')

    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      setStatus('error')
      return
    }

    setStatus('loading')
    try {
      await post('/api/register', { email, password, confirmPassword })
      setCode('')
      setCooldown(60)
      setStatus('idle')
      setStep('code')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
      setStatus('error')
    }
  }

  async function handleCodeSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')

    if (!/^\d{6}$/.test(code)) {
      setError('Please enter the 6-digit code from the email.')
      setStatus('error')
      return
    }

    setStatus('loading')
    try {
      await post('/api/verify', { email, code })
      setStatus('idle')
      setStep('success')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
      setStatus('error')
    }
  }

  async function handleResend() {
    setError('')
    setStatus('loading')
    try {
      await post('/api/resend-code', { email })
      setCooldown(60)
      setStatus('idle')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not resend the code.')
      setStatus('error')
    }
  }

  if (step === 'success') {
    return (
      <div className="flex flex-col items-center gap-4 rounded-2xl border border-border bg-card p-8 text-center shadow-sm">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/15 text-primary">
          <CheckCircle2 className="h-6 w-6" />
        </span>
        <div>
          <h2 className="text-lg font-semibold">Email confirmed</h2>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
            Thanks for confirming <span className="text-foreground">{email}</span>. Your account is
            ready and you now have access to the messaging forums.
          </p>
        </div>
        <Button variant="outline" nativeButton={false} render={<a href="/" />}>
          Back to home
        </Button>
      </div>
    )
  }

  if (step === 'code') {
    return (
      <form onSubmit={handleCodeSubmit} className="flex flex-col gap-4">
        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-primary">
              <Mail className="h-5 w-5" />
            </span>
            <div>
              <h2 className="text-lg font-semibold">Check your email</h2>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                We sent a 6-digit confirmation code to <span className="text-foreground">{email}</span>.
                Enter it below to finish creating your account.
              </p>
            </div>
          </div>

          <div className="mt-5">
            <Field label="Confirmation code" htmlFor="register-code" hint="Expires in 15 minutes">
              <input
                ref={codeInputRef}
                id="register-code"
                type="text"
                required
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                pattern="[0-9]{6}"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="123456"
                className={cn(inputClass, 'text-center font-mono text-lg tracking-[0.4em]')}
              />
            </Field>
          </div>

          {status === 'error' && error && (
            <div
              role="alert"
              className="mt-4 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="mt-5 flex items-center justify-between gap-3">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setStatus('idle')
                setError('')
                setStep('details')
              }}
            >
              <ArrowLeft className="h-4 w-4" />
              Change email
            </Button>
            <Button type="submit" disabled={status === 'loading'}>
              {status === 'loading' && <Loader2 className="h-4 w-4 animate-spin" />}
              Confirm code
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <span>Didn&apos;t get it?</span>
          <Button
            type="button"
            variant="link"
            size="sm"
            className="px-1"
            disabled={cooldown > 0 || status === 'loading'}
            onClick={handleResend}
          >
            Resend code{cooldown > 0 ? ` in ${cooldown}s` : ''}
          </Button>
        </div>
      </form>
    )
  }

  return (
    <form onSubmit={handleDetailsSubmit} className="flex flex-col gap-4">
      <Field label="Email address" htmlFor="register-email">
        <input
          id="register-email"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className={inputClass}
        />
      </Field>

      <Field label="Password" htmlFor="register-password" hint="At least 8 characters">
        <div className="relative">
          <input
            id="register-password"
            type={showPassword ? 'text' : 'password'}
            required
            minLength={8}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className={cn(inputClass, 'pr-10')}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-lg text-muted-foreground transition-colors hover:text-foreground"
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </Field>

      <Field label="Confirm password" htmlFor="register-confirm">
        <input
          id="register-confirm"
          type={showPassword ? 'text' : 'password'}
          required
          minLength={8}
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="••••••••"
          className={inputClass}
        />
      </Field>

      {status === 'error' && error && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <Button type="submit" size="lg" disabled={status === 'loading'}>
        {status === 'loading' && <Loader2 className="h-4 w-4 animate-spin" />}
        Create account
      </Button>
    </form>
  )
}

function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string
  htmlFor: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label htmlFor={htmlFor} className="flex flex-col gap-1.5">
      <span className="flex items-center justify-between text-sm font-medium">
        {label}
        {hint && <span className="text-xs font-normal text-muted-foreground">{hint}</span>}
      </span>
      {children}
    </label>
  )
}
